/**
 * District Athletic — Cinematic Interactions
 * Premium Heritage Athletic Apparel · Est. 1948
 */

(function () {
  'use strict';

  /* ----------------------------------------------------------
     UTILITY: RAF throttle
  ---------------------------------------------------------- */
  function rafThrottle(fn) {
    var ticking = false;
    return function () {
      if (!ticking) {
        ticking = true;
        window.requestAnimationFrame(function () {
          fn();
          ticking = false;
        });
      }
    };
  }

  /* ----------------------------------------------------------
     1. TEXT SLAM — Split headlines into chars/words for animation
  ---------------------------------------------------------- */
  function splitAndSlamText() {
    // Target big headings for char-by-char slam
    var charTargets = document.querySelectorAll(
      'h1:not([data-slam-done]), h2:not([data-slam-done])'
    );

    charTargets.forEach(function (el) {
      // Skip tiny headings and already-processed
      if (el.dataset.slamDone || el.textContent.trim().length < 3) return;

      var text = el.textContent.trim();
      var words = text.split(/\s+/);

      if (words.length === 0) return;

      // Decide: char-slam for short hero titles, word-slam for longer
      var isHero = !!el.closest('[data-section-type="hero"], .hero__blocks, .hero-section, .shopify-section:first-child');
      var mode = (isHero && text.length < 40) ? 'char' : 'word';

      el.setAttribute('data-slam', '');
      el.setAttribute('data-slam-mode', mode);
      el.dataset.slamDone = '1';

      if (mode === 'char') {
        // Wrap each char in a span, words in a clip container
        el.innerHTML = words.map(function (word) {
          var chars = word.split('').map(function (char, i) {
            return '<span class="da-split-char" style="transition-delay:' + (i * 0.038) + 's">' + (char === ' ' ? '&nbsp;' : char) + '</span>';
          }).join('');
          return '<span class="da-split-word" aria-hidden="true">' + chars + '</span>';
        }).join('<span class="da-split-word-gap">&nbsp;</span>');
      } else {
        // Word-by-word slam
        el.setAttribute('data-slam-words', '');
        el.removeAttribute('data-slam');
        el.innerHTML = words.map(function (word, i) {
          return '<span class="da-split-word" style="transition-delay:' + (i * 0.07) + 's">' + word + '</span>';
        }).join(' ');
      }
    });

    // Mark subheadings for fade-slide
    document.querySelectorAll(
      'h3:not([data-reveal-sub-done]), .da-subheading:not([data-reveal-sub-done])'
    ).forEach(function (el) {
      if (el.dataset.revealSubDone) return;
      el.setAttribute('data-reveal-sub', '');
      el.dataset.revealSubDone = '1';
    });
  }

  /* ----------------------------------------------------------
     2. INTERSECTION OBSERVER — Trigger slam animations
  ---------------------------------------------------------- */
  function initScrollAnimations() {
    // Slam observer (threshold low so big headlines trigger early)
    var slamObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-slammed');
          slamObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });

    // Generic reveal observer
    var revealObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          revealObserver.unobserve(entry.target);
        }
      });
    }, { threshold: 0.06, rootMargin: '0px 0px -40px 0px' });

    // Observe slam targets
    document.querySelectorAll('[data-slam], [data-slam-words]').forEach(function (el) {
      // Skip if already in viewport at load
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.9) {
        el.classList.add('is-slammed');
      } else {
        slamObserver.observe(el);
      }
    });

    // Observe generic reveals
    document.querySelectorAll('[data-reveal-sub], .da-reveal, .da-reveal-group').forEach(function (el) {
      var rect = el.getBoundingClientRect();
      if (rect.top < window.innerHeight * 0.88) {
        el.classList.add('is-visible');
      } else {
        revealObserver.observe(el);
      }
    });

    // Mark section cards for staggered reveal
    document.querySelectorAll(
      '.product-grid, .collection-grid, [class*="product-list"]'
    ).forEach(function (grid) {
      if (!grid.dataset.revealDone) {
        grid.classList.add('da-reveal-group');
        grid.dataset.revealDone = '1';
        revealObserver.observe(grid);
      }
    });
  }

  /* ----------------------------------------------------------
     3. HERO PARALLAX — Cinematic depth on scroll
  ---------------------------------------------------------- */
  function initHeroParallax() {
    var heroSections = document.querySelectorAll(
      '.shopify-section:has(.hero__media-wrapper), [data-section-type="hero"]'
    );

    if (!heroSections.length || window.innerWidth < 768) return;

    heroSections.forEach(function (hero) {
      var media = hero.querySelector('img, video');
      if (!media) return;

      // Ensure the overlay exists
      var wrapper = hero.querySelector('.hero__media-wrapper');
      if (wrapper && !wrapper.querySelector('.da-hero-overlay')) {
        var overlay = document.createElement('div');
        overlay.className = 'da-hero-overlay';
        wrapper.appendChild(overlay);
      }

      window.addEventListener('scroll', rafThrottle(function () {
        var rect = hero.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) return;

        var progress = -rect.top / window.innerHeight;
        var parallax = progress * 120; // px offset
        media.style.transform = 'translateY(' + parallax + 'px) scale(1.12)';
      }), { passive: true });
    });
  }

  /* ----------------------------------------------------------
     4. PAGE TRANSITIONS — Smooth route changes
  ---------------------------------------------------------- */
  function initPageTransitions() {
    function navigate(href) {
      window.location.href = href;
    }

    function isInternalLink(link, e) {
      var href = link.getAttribute('href');
      if (!href ||
          href.startsWith('#') ||
          href.startsWith('mailto:') ||
          href.startsWith('tel:') ||
          link.getAttribute('target') === '_blank' ||
          e.metaKey || e.ctrlKey || e.shiftKey) return false;
      try {
        var url = new URL(href, window.location.href);
        return url.origin === window.location.origin;
      } catch (err) {
        return false;
      }
    }

    document.addEventListener('click', function (e) {
      var link = e.target.closest('a[href]');
      if (!link || !isInternalLink(link, e)) return;

      var href = link.getAttribute('href');
      e.preventDefault();

      if (document.startViewTransition) {
        // startViewTransition with a real navigation preserves fbclid/utm params
        // and ensures the Meta pixel fires correctly on every page load.
        document.startViewTransition(function () {
          navigate(href);
        });
      } else {
        var overlay = document.querySelector('.da-page-transition');
        if (overlay) {
          overlay.classList.add('is-entering');
          setTimeout(function () { navigate(href); }, 520);
        } else {
          navigate(href);
        }
      }
    });

    window.addEventListener('pageshow', function () {
      var overlay = document.querySelector('.da-page-transition');
      if (!overlay) return;
      overlay.classList.remove('is-entering');
      overlay.classList.add('is-leaving');
      setTimeout(function () { overlay.classList.remove('is-leaving'); }, 600);
    });
  }

  /* ----------------------------------------------------------
     5. MARQUEE — Cinematic scrolling strip between sections
  ---------------------------------------------------------- */
  function initMarquee() {
    // Inject DA marquee strips between every 3rd section
    injectMarqueeStrips();

    // Also enhance existing Horizon marquee sections
    document.querySelectorAll(
      '.marquee__track, [class*="marquee__track"], marquee-section .track'
    ).forEach(function (track) {
      if (track.dataset.daMarqueeInit) return;
      track.dataset.daMarqueeInit = '1';

      // Clone for seamless loop
      if (!track.dataset.cloned) {
        var original = track.innerHTML;
        track.innerHTML = original + original;
        track.dataset.cloned = '1';
      }

      // Pause on hover
      var section = track.closest('.shopify-section, marquee-section');
      if (section) {
        section.addEventListener('mouseenter', function () {
          track.style.animationPlayState = 'paused';
        });
        section.addEventListener('mouseleave', function () {
          track.style.animationPlayState = 'running';
        });
      }
    });
  }

  function injectMarqueeStrips() {
    // Skip on collection pages: the grid + reviews section replace the slogan strip there
    var mainEl = document.getElementById('MainContent');
    if (mainEl && mainEl.dataset.template && mainEl.dataset.template.indexOf('collection') === 0) return;
    var sections = document.querySelectorAll('#MainContent > .shopify-section');
    var stripTexts = [
      'District Athletic',
      'Est. 1948',
      'Premium Heritage',
      'Athletic Apparel',
      'Crafted for Champions',
      'American Heritage',
    ];

    // Insert after the 2nd section if not already there
    var insertAfter = sections[1];
    if (!insertAfter || document.querySelector('.da-marquee-section')) return;

    var strip = document.createElement('div');
    strip.className = 'da-marquee-section';
    strip.setAttribute('aria-hidden', 'true');

    var track = document.createElement('div');
    track.className = 'da-marquee-track';

    // Repeat content enough times for seamless loop
    var items = '';
    for (var r = 0; r < 4; r++) {
      stripTexts.forEach(function (text) {
        items += '<span class="da-marquee-item">' +
          '<span class="da-marquee-dot"></span>' +
          text +
          '</span>';
      });
    }
    track.innerHTML = items;
    strip.appendChild(track);

    insertAfter.after(strip);
  }

  /* ----------------------------------------------------------
     6. HEADER — Transparent to opaque on scroll
  ---------------------------------------------------------- */
  function initHeaderScroll() {
    var header = document.querySelector('#header-group');
    if (!header) return;

    var lastScroll = 0;

    window.addEventListener('scroll', rafThrottle(function () {
      var current = window.pageYOffset;
      if (current > 80) {
        header.classList.add('header--scrolled');
      } else {
        header.classList.remove('header--scrolled');
      }

      // Mobile hide-on-scroll removed: the theme's built-in sticky header
      // already manages visibility, and the extra transform caused a
      // see-through gap above the header on iOS.
      lastScroll = current <= 0 ? 0 : current;
    }), { passive: true });
  }

  /* ----------------------------------------------------------
     7. PRODUCT CARD HOVERS — Prep will-change
  ---------------------------------------------------------- */
  function initCardHover() {
    document.querySelectorAll('.product-card, .card').forEach(function (card) {
      card.addEventListener('mouseenter', function () {
        this.style.willChange = 'transform, box-shadow';
        var img = this.querySelector('.media img');
        if (img) img.style.willChange = 'transform';
      });

      card.addEventListener('mouseleave', function () {
        this.style.willChange = '';
        var img = this.querySelector('.media img');
        if (img) img.style.willChange = '';
      });
    });
  }

  /* ----------------------------------------------------------
     8. CART COUNT ANIMATION
  ---------------------------------------------------------- */
  function initCartCountAnimation() {
    var cartCount = document.querySelector('.cart-count, [data-cart-count]');
    if (!cartCount) return;

    new MutationObserver(function () {
      cartCount.classList.add('cart-count--bump');
      setTimeout(function () {
        cartCount.classList.remove('cart-count--bump');
      }, 400);
    }).observe(cartCount, { childList: true, subtree: true });
  }

  /* ----------------------------------------------------------
     9. SMOOTH ANCHOR SCROLL
  ---------------------------------------------------------- */
  function initSmoothScroll() {
    document.querySelectorAll('a[href^="#"]').forEach(function (anchor) {
      anchor.addEventListener('click', function (e) {
        var target = document.querySelector(this.getAttribute('href'));
        if (target) {
          e.preventDefault();
          target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      });
    });
  }

  /* ----------------------------------------------------------
     10. PRODUCT PAGE — Gallery zoom
  ---------------------------------------------------------- */
  function initProductImageZoom() {
    var gallery = document.querySelector('.product-media-gallery, .media-gallery');
    if (!gallery) return;

    gallery.querySelectorAll('.media').forEach(function (mediaEl) {
      var img = mediaEl.querySelector('img');
      if (!img) return;

      mediaEl.addEventListener('mouseenter', function () {
        img.style.transform = 'scale(1.05)';
      });
      mediaEl.addEventListener('mouseleave', function () {
        img.style.transform = 'scale(1)';
      });
    });
  }

  /* ----------------------------------------------------------
     11. PAGE TRANSITION OVERLAY — inject into DOM
  ---------------------------------------------------------- */
  function injectTransitionOverlay() {
    if (document.querySelector('.da-page-transition')) return;
    var overlay = document.createElement('div');
    overlay.className = 'da-page-transition';
    document.body.insertBefore(overlay, document.body.firstChild);
  }

  /* ----------------------------------------------------------
     12. BRAND CLASS MARKERS
  ---------------------------------------------------------- */
  function addBrandClasses() {
    document.querySelectorAll(
      '.hero__blocks h1, .hero__blocks h2, [data-section-type="hero"] h1'
    ).forEach(function (el) {
      el.classList.add('da-hero-title');
    });
  }

  /* ----------------------------------------------------------
     13. INIT ALL
  ---------------------------------------------------------- */
  function initAll() {
    splitAndSlamText();
    initScrollAnimations();
    initHeroParallax();
    initCardHover();
    initSmoothScroll();
    initHeaderScroll();
    addBrandClasses();
    initCartCountAnimation();
    initProductImageZoom();
    initMarquee();
  }

  function init() {
    injectTransitionOverlay();
    initPageTransitions();
    initAll();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section changes in theme editor
  document.addEventListener('shopify:section:load', function () {
    splitAndSlamText();
    initScrollAnimations();
    initProductImageZoom();
    initMarquee();
    addBrandClasses();
  });

})();

/* ==========================================================
   District Athletic — Commerce
   Free-gift auto-add, cart upsell, estimated delivery dates,
   and a reveal-animation safety net. Vanilla JS, no deps.
   ========================================================== */
(function () {
  'use strict';

  var SOURCE = 'da-commerce';

  /* ----------------------------------------------------------
     Reveal safety net — if the IntersectionObservers above ever
     miss an element (prerendered/background tab, morphed DOM),
     reveal anything already in the viewport so headlines can
     never stay invisible.
  ---------------------------------------------------------- */
  function revealSweep() {
    var vh = window.innerHeight || document.documentElement.clientHeight;
    if (!vh) return;
    var hidden = document.querySelectorAll(
      '[data-slam]:not(.is-slammed), [data-slam-words]:not(.is-slammed)'
    );
    hidden.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) el.classList.add('is-slammed');
    });
    var reveals = document.querySelectorAll(
      '[data-reveal-sub]:not(.is-visible), .da-reveal:not(.is-visible), .da-reveal-group:not(.is-visible)'
    );
    reveals.forEach(function (el) {
      var r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) el.classList.add('is-visible');
    });
  }

  function initRevealSafetyNet() {
    var ticking = false;
    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      setTimeout(function () {
        revealSweep();
        ticking = false;
      }, 200);
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', revealSweep);
    window.addEventListener('pageshow', revealSweep);
    var runs = 0;
    var warmup = setInterval(function () {
      revealSweep();
      runs += 1;
      if (runs >= 5) clearInterval(warmup);
    }, 1200);
  }

  /* ----------------------------------------------------------
     Shared cart helpers
  ---------------------------------------------------------- */
  function getCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('cart fetch failed');
      return r.json();
    });
  }

  function postJson(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
  }

  function dispatchCartUpdate(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: cart,
          sourceId: SOURCE,
          data: { source: SOURCE, itemCount: cart.item_count },
        },
      })
    );
  }

  function isGiftLine(item) {
    return !!(item.properties && (item.properties._gift === 'true' || item.properties._gift === true));
  }

  /* ----------------------------------------------------------
     Free gift auto-add / auto-remove
     Config comes from [data-da-gift-config] rendered by
     snippets/da-free-shipping-bar.liquid. Only lines tagged with
     the _gift property are ever removed automatically.
  ---------------------------------------------------------- */
  var giftBusy = false;
  var giftFailedAt = 0;

  function giftConfig() {
    var el = document.querySelector('[data-da-gift-config]');
    if (!el) return null;
    return {
      threshold: parseInt(el.getAttribute('data-threshold-cents'), 10) || 0,
      productId: parseInt(el.getAttribute('data-gift-product-id'), 10) || 0,
      variantId: parseInt(el.getAttribute('data-gift-variant-id'), 10) || 0,
      available: el.getAttribute('data-gift-available') === 'true',
      autoAdd: el.getAttribute('data-auto-add') === 'true',
    };
  }

  function evaluateGift(event) {
    var cfg = giftConfig();
    if (!cfg || !cfg.autoAdd || !cfg.threshold || !cfg.variantId || !cfg.productId) return;
    if (event && event.detail && event.detail.data && event.detail.data.source === SOURCE) return;
    if (giftBusy) return;
    giftBusy = true;

    getCart()
      .then(function (cart) {
        var eligible = 0;
        var giftLineKeys = [];
        var giftInCart = false;

        cart.items.forEach(function (item) {
          if (item.product_id === cfg.productId) {
            giftInCart = true;
            if (isGiftLine(item)) giftLineKeys.push(item.key);
          } else {
            eligible += item.final_line_price;
          }
        });

        var unlocked = eligible >= cfg.threshold;

        // Respect a shopper who removed the free gift themselves: only
        // re-add after the cart drops below the threshold and crosses it
        // again.
        if (unlocked && !giftInCart && sessionStorage.getItem('daGiftOptOut') === '1') {
          return null;
        }
        if (!unlocked) {
          sessionStorage.removeItem('daGiftOptOut');
        }
        if (unlocked && !giftInCart && sessionStorage.getItem('daGiftWasInCart') === '1') {
          sessionStorage.setItem('daGiftOptOut', '1');
          sessionStorage.removeItem('daGiftWasInCart');
          return null;
        }

        if (unlocked && !giftInCart && cfg.available) {
          if (giftFailedAt && Date.now() - giftFailedAt < 30000) return null;
          return postJson('/cart/add.js', {
            items: [{ id: cfg.variantId, quantity: 1, properties: { _gift: 'true' } }],
          }).then(function (r) {
            if (!r.ok) {
              giftFailedAt = Date.now();
              throw new Error('gift add failed');
            }
            sessionStorage.setItem('daGiftWasInCart', '1');
            return getCart().then(dispatchCartUpdate);
          });
        }

        if (!unlocked && giftLineKeys.length) {
          var chain = Promise.resolve();
          giftLineKeys.forEach(function (key) {
            chain = chain.then(function () {
              return postJson('/cart/change.js', { id: key, quantity: 0 });
            });
          });
          return chain.then(function () {
            sessionStorage.removeItem('daGiftWasInCart');
            return getCart().then(dispatchCartUpdate);
          });
        }

        if (giftInCart && giftLineKeys.length) {
          sessionStorage.setItem('daGiftWasInCart', '1');
        }
        return null;
      })
      .catch(function (err) {
        console.warn('[DA] gift auto-add:', err);
      })
      .then(function () {
        giftBusy = false;
      });
  }

  /* ----------------------------------------------------------
     Cart upsell — one-tap add + per-session dismiss
     Markup comes from snippets/da-cart-upsell.liquid.
  ---------------------------------------------------------- */
  function applyUpsellDismissed() {
    if (sessionStorage.getItem('daUpsellDismissed') !== '1') return;
    document.querySelectorAll('[data-da-upsell]').forEach(function (mod) {
      mod.hidden = true;
    });
  }

  function initUpsell() {
    document.addEventListener('click', function (e) {
      var dismiss = e.target.closest('[data-da-upsell-dismiss]');
      if (dismiss) {
        sessionStorage.setItem('daUpsellDismissed', '1');
        applyUpsellDismissed();
        return;
      }

      var btn = e.target.closest('[data-da-upsell-add]');
      if (!btn) return;
      e.preventDefault();

      var item = btn.closest('[data-da-upsell-item]');
      var select = item && item.querySelector('select[data-da-upsell-variant]');
      var variantId = parseInt((select && select.value) || btn.getAttribute('data-variant-id') || '', 10);
      if (!variantId) return;

      btn.disabled = true;
      postJson('/cart/add.js', { items: [{ id: variantId, quantity: 1 }] })
        .then(function (r) {
          if (!r.ok) throw new Error('upsell add failed');
          return getCart().then(dispatchCartUpdate);
        })
        .catch(function (err) {
          console.warn('[DA] upsell add:', err);
        })
        .then(function () {
          btn.disabled = false;
        });
    });

    applyUpsellDismissed();
  }

  /* ----------------------------------------------------------
     Estimated delivery dates
     Order date + dispatch window + shipping window, business
     days only (weekends skipped). Formats like "Jul 24".
  ---------------------------------------------------------- */
  function addBusinessDays(start, days) {
    var d = new Date(start.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      var day = d.getDay();
      if (day !== 0 && day !== 6) added += 1;
    }
    return d;
  }

  function formatShortDate(d) {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  }

  function initDeliveryEstimate() {
    document.querySelectorAll('[data-da-delivery]').forEach(function (el) {
      if (el.dataset.daDeliveryDone) return;
      var dispatchMin = parseInt(el.getAttribute('data-dispatch-min'), 10) || 3;
      var dispatchMax = parseInt(el.getAttribute('data-dispatch-max'), 10) || 7;
      var shipMin = parseInt(el.getAttribute('data-ship-min'), 10) || 5;
      var shipMax = parseInt(el.getAttribute('data-ship-max'), 10) || 8;

      var today = new Date();
      var from = addBusinessDays(today, dispatchMin + shipMin);
      var to = addBusinessDays(today, dispatchMax + shipMax);

      var target = el.querySelector('[data-da-delivery-text]') || el;
      target.textContent = 'Order today, estimated delivery ' + formatShortDate(from) + ' to ' + formatShortDate(to);
      el.hidden = false;
      el.dataset.daDeliveryDone = '1';
    });
  }

  /* ----------------------------------------------------------
     Wire up
  ---------------------------------------------------------- */
  function init() {
    initRevealSafetyNet();
    initUpsell();
    initDeliveryEstimate();
    evaluateGift();

    document.addEventListener('cart:update', function (event) {
      evaluateGift(event);
      setTimeout(applyUpsellDismissed, 350);
      setTimeout(applyUpsellDismissed, 1200);
    });

    document.addEventListener('shopify:section:load', function () {
      initDeliveryEstimate();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
